import React from "react";
import "@/styles/why-us.css";
import { siteSettings } from "@workspace/utils";

/**
 * "Why are we better than Others" comparison table.
 *
 * Rendered on every program page (MiM, MBA, MFin, …) just above the Pricing
 * section. The `#why-us` id is the scroll target for the program hero / final
 * CTA "Why US?" buttons.
 *
 * Content mirrors the GradBrew-vs-typical-consultancy reference; the layout is
 * a responsive CSS grid (3 columns on desktop, stacked cards on mobile) so no
 * horizontal scroll is needed and it stays readable in light and dark themes.
 */

type ComparisonRow = {
    factor: string;
    gradbrew: string;
    others: string;
};

const comparisonRows: ComparisonRow[] = [
    {
        factor: "Consulting Philosophy",
        gradbrew: "Ethically driven consulting focused on developing authentic applicants.",
        others: "Sales-driven consulting focused on closing deals.",
    },
    {
        factor: "Application Writing",
        gradbrew: "No ghostwriters, content writers, or AI essays.",
        others: "Essays often written by ghostwriters, content teams, or AI.",
    },
    {
        factor: "Consultant Involvement",
        gradbrew: "Maximum consultant hours with deep mentorship.",
        others: "Limited consultant time, much work delegated to juniors.",
    },
    {
        factor: "Mentor Background",
        gradbrew: "Mentors are graduates of top global universities with real admissions exposure.",
        others: "Many coaches or founders have limited international admissions experience.",
    },
    {
        factor: "Application Review",
        gradbrew: "Dual-consultant review before every submission.",
        others: "Often handled by a single consultant or editing team.",
    },
    {
        factor: "School Strategy",
        gradbrew: "Focus on competitive & dream schools.",
        others: "“Safe-school dumping” to inflate success statistics.",
    },
    {
        factor: "Feedback Style",
        gradbrew: "Honest feedback from Day 1 about strengths and gaps.",
        others: "Sugarcoated feedback to keep clients comfortable.",
    },
    {
        factor: "Interview Preparation",
        gradbrew: "Realistic mock interviews reflecting actual school interviews.",
        others: "Generic interview practice with limited depth.",
    },
    {
        factor: "Coaching Method",
        gradbrew: "No templates — we build your real story.",
        others: "Template-based applications reused across candidates.",
    },
    {
        factor: "Operating Model",
        gradbrew: "Direct interaction with experienced mentors.",
        others: "Sales teams or coordinators manage clients.",
    },
    {
        factor: "Candidate Philosophy",
        gradbrew: "You are an applicant with ambitions and goals.",
        others: "You are treated as just another paying client.",
    },
];

function CheckIcon() {
    return (
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 20 20" fill="none" className="why-us_icon why-us_icon--check">
            <path d="M16.667 5 7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CrossIcon() {
    return (
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 20 20" fill="none" className="why-us_icon why-us_icon--cross">
            <path d="m15 5-10 10M5 5l10 10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function WhyUs() {
    return (
        <section
            id="why-us"
            aria-labelledby="why-us-heading"
            className="section_why-us bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]"
        >
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-vertical padding-xhuge">
                        <div className="max-width-large align-center text-align-center">
                            <h2
                                id="why-us-heading"
                                className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]"
                            >
                                Why are we better than Others?
                            </h2>
                            <div className="padding-bottom padding-xsmall"></div>
                            <p className="text-size-medium">
                                {siteSettings.siteName} vs typical admission consultancies —
                                across the decisions that actually shape your admit.
                            </p>
                            <div className="padding-bottom padding-large"></div>
                        </div>

                        <div className="why-us_table" role="table" aria-label={`${siteSettings.siteName} versus typical admission consultancies`}>
                            {/* Column headers (desktop only) */}
                            <div className="why-us_row why-us_row--head" role="row">
                                <div className="why-us_cell why-us_cell--factor" role="columnheader">
                                    Decision Factor
                                </div>
                                <div className="why-us_cell why-us_cell--gradbrew why-us_col-head" role="columnheader">
                                    {siteSettings.siteName}
                                </div>
                                <div className="why-us_cell why-us_cell--others why-us_col-head" role="columnheader">
                                    Typical Admission Consultancies
                                </div>
                            </div>

                            {comparisonRows.map((row) => (
                                <div className="why-us_row" role="row" key={row.factor}>
                                    <div className="why-us_cell why-us_cell--factor" role="rowheader">
                                        {row.factor}
                                    </div>
                                    <div className="why-us_cell why-us_cell--gradbrew" role="cell">
                                        <span className="why-us_mobile-label">{siteSettings.siteName}</span>
                                        <span className="why-us_value">
                                            <CheckIcon />
                                            <span>{row.gradbrew}</span>
                                        </span>
                                    </div>
                                    <div className="why-us_cell why-us_cell--others" role="cell">
                                        <span className="why-us_mobile-label">Typical Consultancies</span>
                                        <span className="why-us_value">
                                            <CrossIcon />
                                            <span>{row.others}</span>
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default WhyUs;
